import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Conversation_Kind } from '@gen/chat/v1/common_pb';
import { useStrings } from '@/i18n/Localization';
import { listConversations, listDrafts } from '@/store/localStore';
import { useLiveQuery } from '@/store/hooks';
import { NewConversationDialog } from '@/screens/NewConversationDialog';
import { JoinDialog, NewChannelDialog, NewGroupDialog } from '@/screens/NewGroupDialog';
import type { ConversationRecord, DraftRecord } from '@/store/types';
import { Avatar } from '@/components/Avatar';
import styles from '@/screens/InboxScreen.module.css';

/**
 * The inbox: search, new conversation, the dialog list with timestamps, draft
 * previews and unread badges.
 *
 * Search filters what is already on this device. It is not a server search —
 * that is `SearchHistory` in Phase 2 — and the distinction matters because a
 * stale local projection must never look like an authoritative answer about
 * what exists. Filtering a visible list is honest; presenting the same list as
 * "all results" would not be.
 */
export function InboxScreen() {
  const { t, formatRelativeDay } = useStrings();
  const navigate = useNavigate();
  const params = useParams<{ conversationId?: string }>();
  const [search, setSearch] = useState('');
  /** Which creation flow is open, if any. Only one can be at a time. */
  const [creating, setCreating] = useState<'direct' | 'group' | 'channel' | 'join' | null>(null);

  const conversations = useLiveQuery<ConversationRecord[]>(listConversations, [], []);
  const drafts = useLiveQuery<DraftRecord[]>(listDrafts, [], []);

  const draftsById = useMemo(() => {
    const map = new Map<string, DraftRecord>();
    for (const draft of drafts) {
      if (draft.text.trim().length > 0) {
        map.set(draft.conversationId, draft);
      }
    }
    return map;
  }, [drafts]);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const withAccess = conversations.filter((conversation) => !conversation.accessRevoked);
    if (needle.length === 0) {
      return withAccess;
    }
    return withAccess.filter(
      (conversation) =>
        conversation.title.toLocaleLowerCase().includes(needle) ||
        conversation.lastMessagePreview.toLocaleLowerCase().includes(needle),
    );
  }, [conversations, search]);

  /**
   * Saved Messages, the Section 06 private self-conversation.
   *
   * It only exists once the server has told this account about it, which is why
   * the entry is conditional rather than a permanent link that could lead
   * nowhere. It is pulled out of the ordinary list so it does not sink below
   * recent traffic.
   */
  const saved = useMemo(
    () => conversations.find((conversation) => conversation.kind === Conversation_Kind.SAVED) ?? null,
    [conversations],
  );

  return (
    <nav className={styles.inbox} aria-label={t('nav.inbox')}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('inbox.title')}</h1>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              setCreating('direct');
            }}
          >
            {t('inbox.newConversation')}
          </button>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              setCreating('group');
            }}
          >
            {t('group.create')}
          </button>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              setCreating('channel');
            }}
          >
            {t('channel.create')}
          </button>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              setCreating('join');
            }}
          >
            {t('invite.join')}
          </button>
          <Link className={styles.iconButton} to="/settings">
            {t('nav.settings')}
          </Link>
        </div>
      </div>

      {saved === null ? null : (
        <Link className={styles.iconButton} to={`/c/${saved.id}`}>
          {t('saved.open')}
        </Link>
      )}

      <div className={styles.searchRow}>
        <label className={styles.visuallyHidden} htmlFor="inbox-search">
          {t('inbox.search')}
        </label>
        <input
          id="inbox-search"
          className={styles.search}
          type="search"
          dir="auto"
          value={search}
          placeholder={t('inbox.searchPlaceholder')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>

      {visible.length === 0 ? (
        <p className={styles.empty}>
          {search.trim().length > 0 ? t('inbox.emptySearch') : t('inbox.empty')}
        </p>
      ) : (
        <ul className={styles.list}>
          {visible.map((conversation) => {
            const draft = draftsById.get(conversation.id);
            const selected = params.conversationId === conversation.id;
            const preview = draft?.text ?? conversation.lastMessagePreview;

            return (
              <li key={conversation.id}>
                <Link
                  to={`/c/${conversation.id}`}
                  className={`${styles.item} ${selected ? styles.selected : ''}`}
                  aria-current={selected ? 'page' : undefined}
                  aria-label={t('inbox.openConversation', {
                    title: conversation.title || t('conversation.unknownSender'),
                  })}
                >
                  <Avatar
                    id={conversation.id}
                    name={conversation.title || t('conversation.unknownSender')}
                    size="md"
                  />
                  <div className={styles.itemBody}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemTitle} dir="auto">
                        {conversation.title || t('conversation.unknownSender')}
                      </span>
                      <time
                        className={styles.itemTime}
                        dateTime={new Date(conversation.lastMessageAt).toISOString()}
                      >
                        {conversation.lastMessageAt > 0
                          ? formatRelativeDay(conversation.lastMessageAt)
                          : ''}
                      </time>
                    </div>
                    <div className={styles.itemBottom}>
                      <span className={styles.itemPreview} dir="auto">
                        {draft ? (
                          <span className={styles.draftTag}>{t('inbox.draftPrefix')} </span>
                        ) : null}
                        {preview.length > 0 ? preview : t('inbox.noMessages')}
                      </span>
                      {conversation.unreadCount > 0 || conversation.unreadCapped ? (
                        <span
                          className={styles.badge}
                          // The badge is a number on screen and a sentence to a
                          // screen reader; "3" alone would be meaningless read out.
                          aria-label={
                            conversation.unreadCapped
                              ? t('inbox.unreadCapped')
                              : t('inbox.unread', { count: conversation.unreadCount })
                          }
                        >
                          {conversation.unreadCapped ? '99+' : conversation.unreadCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {creating === 'direct' ? (
        <NewConversationDialog
          onOpened={(conversationId) => {
            setCreating(null);
            void navigate(`/c/${conversationId}`);
          }}
          onClose={() => {
            setCreating(null);
          }}
        />
      ) : null}

      {creating === 'group' ? (
        <NewGroupDialog
          onCreated={(conversationId) => {
            setCreating(null);
            void navigate(`/c/${conversationId}`);
          }}
          onClose={() => {
            setCreating(null);
          }}
        />
      ) : null}

      {creating === 'channel' ? (
        <NewChannelDialog
          onCreated={(conversationId) => {
            setCreating(null);
            void navigate(`/c/${conversationId}`);
          }}
          onClose={() => {
            setCreating(null);
          }}
        />
      ) : null}

      {creating === 'join' ? (
        <JoinDialog
          onClose={() => {
            setCreating(null);
          }}
        />
      ) : null}
    </nav>
  );
}
